import { useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { DatePickerAtom, DropdownAtom, ModalAtom, TextAreaAtom, TextFieldAtom } from "../../../components/atoms/atom";
import { isInviteAccepted } from "../../../constants/invite-status";
import {
    TICKET_STATUS,
    getTicketStatusOptions,
    getTicketTypeOptions
} from "../../../constants/ticket-types";
import useProjectPermission from "../../../hooks/useProjectPermission";
import { useGetProjectMembersQuery } from "../../../store/services/projectMembers";
import { useGetProjectTicketCategoriesQuery } from "../../../store/services/ticketCategoriesService";
import {
    getTicketAssigneeId,
    getTicketOwnerId,
} from "../../../utils/projectPermissionHelpers";

const initialFormState = {
    projectTicketCategoryId: "",
    ticketType: "",
    summary: "",
    description: "",
    amount: "",
    status: TICKET_STATUS.OPEN,
    deadline: null,
    assignedTo: "",
};

const formatMemberOptionLabel = (member) => {
    const user = member?.user ?? {};
    const name = [user.firstName, user.lastName].filter(Boolean).join(" ");
    const email = user.email || "";
    if (name && email) return `${name} - ${email}`;
    return name || email || member.userId;
};

const TicketFormModal = ({
    show,
    onHide,
    onSubmit,
    onApprove,
    ticket = null,
    projectId,
    categoryId = null,
    loading = false,
    canEditAssignee = true,
    canEditTicket = true,
    canApprove = false,
    requireTicketApproval = false,
}) => {
    const { t } = useTranslation();
    const currentUserId = useSelector((state) => state.auth?.user?.id);
    const [formData, setFormData] = useState(initialFormState);
    const [errors, setErrors] = useState({});

    const { data: categoriesResponse } = useGetProjectTicketCategoriesQuery(
        { projectId },
        { skip: !projectId || !show },
    );
    const {
        data: projectMembersResponse,
    } = useGetProjectMembersQuery(projectId, {
        skip: !projectId || !show,
    });
    const projectMembers = projectMembersResponse?.items ?? [];
    const { filterByCategoryScope } = useProjectPermission();

    const isEditMode = !!ticket;
    const isApproved = !!ticket?.approvedForInvoiceAt;
    const fieldsDisabled = isApproved || loading;
    const showApproveButton =
        isEditMode &&
        canApprove &&
        requireTicketApproval &&
        !isApproved;

    const modalTitle = !isEditMode
        ? t("tickets.add-ticket")
        : isApproved
            ? t("tickets.view-ticket")
            : t("tickets.edit-ticket");

    // Reset form when modal closes
    useEffect(() => {
        if (!show) {
            setFormData(initialFormState);
            setErrors({});
        }
    }, [show]);

    // Initialize form data when modal opens
    useEffect(() => {
        if (show && ticket) {
            setFormData({
                projectTicketCategoryId: ticket.projectTicketCategoryId || "",
                ticketType: ticket.ticketType || "",
                summary: ticket.summary || "",
                description: ticket.description || "",
                amount: ticket.amount || "",
                status: ticket.status || TICKET_STATUS.OPEN,
                deadline: ticket.deadline ? new Date(ticket.deadline) : null,
                assignedTo: getTicketAssigneeId(ticket) || "",
            });
        } else if (show && categoryId) {
            setFormData((prev) => ({
                ...prev,
                projectTicketCategoryId: categoryId,
            }));
        } else if (show && !ticket && !categoryId) {
            setFormData(initialFormState);
        }
    }, [show, ticket, categoryId]);

    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        // Clear error for this field
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: "" }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.projectTicketCategoryId) {
            newErrors.projectTicketCategoryId = t(
                "error-required",
            );
        }

        if (!formData.ticketType) {
            newErrors.ticketType = t("tickets.error-ticket-type-required");
        }

        if (!formData.summary || formData.summary.trim() === "") {
            newErrors.summary = t("tickets.error-summary-required");
        }

        if (!formData.amount || isNaN(parseFloat(formData.amount))) {
            newErrors.amount = t("tickets.error-amount-required");
        }

        // When the project requires approval, every ticket needs an assignee
        // (the designated approver).
        if (requireTicketApproval && !formData.assignedTo) {
            newErrors.assignedTo = t("tickets.error-assignee-required");
        }

        // The assignee (the approver) must not be the ticket's creator. The
        // dropdown already excludes the creator; this backstops it.
        if (formData.assignedTo && formData.assignedTo === creatorId) {
            newErrors.assignedTo = t("tickets.error-assignee-not-creator");
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        // Approved tickets are content-locked; only the workflow status may still
        // change. Skip content validation and send just the status.
        if (isApproved) {
            await onSubmit({ status: formData.status });
            return;
        }

        if (!validateForm()) {
            return;
        }

        const payload = {
            projectId,
            projectTicketCategoryId: formData.projectTicketCategoryId,
            ticketType: formData.ticketType,
            summary: formData.summary.trim(),
            description: formData.description.trim() || null,
            amount: parseFloat(formData.amount),
            status: formData.status,
            deadline: formData.deadline ? formData.deadline.toISOString().split("T")[0] : null,
            assignedTo: formData.assignedTo || null,
        };

        await onSubmit(payload);
    };

    const handleClose = () => {
        setFormData(initialFormState);
        setErrors({});
        onHide();
    };

    const scopedCategories = useMemo(
        () =>
            filterByCategoryScope(
                categoriesResponse?.items ?? [],
                (cat) => cat.id,
            ),
        [filterByCategoryScope, categoriesResponse?.items],
    );

    const categoryOptions = scopedCategories.map((cat) => ({
        value: cat.id,
        label: cat.name,
    }));

    // The assignee is the approver, so it can never be the ticket's creator
    // (separation of duties). On create the creator is the current user; on edit
    // it's the ticket's recorded creator. The creator is excluded from the
    // assignable list so it can't be picked.
    const creatorId = isEditMode
        ? getTicketOwnerId(ticket) || ""
        : currentUserId || "";

    // Only members who have ACCEPTED their invite and whose account is active
    // are assignable - pending/uninvited or deactivated users are excluded.
    // The ticket's existing assignee is always kept (even if since deactivated
    // or removed) so editing doesn't silently drop the current assignment.
    const currentAssigneeId = getTicketAssigneeId(ticket) || "";
    const assignableMembers = (projectMembers || []).filter(
        (member) =>
            member.userId === currentAssigneeId ||
            (member.userId !== creatorId &&
                isInviteAccepted(member) &&
                member.user?.isActive !== false),
    );

    const assigneeOptions = [
        { value: "", label: t("projects.tickets.unassigned") },
        ...assignableMembers.map((member) => ({
            value: member.userId,
            label: formatMemberOptionLabel(member),
        })),
    ];

    const ticketTypeOptions = getTicketTypeOptions(t);
    const statusOptions = getTicketStatusOptions(t);

    const modalBody = (
        <>
            <Row>
                <Col md={12}>
                    <DropdownAtom
                        label={t("projects.ticket-categories")}
                        value={formData.projectTicketCategoryId}
                        options={categoryOptions}
                        onChange={(e) =>
                            handleChange(
                                "projectTicketCategoryId",
                                e.target.value,
                            )
                        }
                        error={errors.projectTicketCategoryId}
                        required
                        disabled={fieldsDisabled || !!categoryId || isEditMode}
                    />
                </Col>
            </Row>

            <Row>
                <Col md={6}>
                    <DropdownAtom
                        label={t("tickets.ticket-type")}
                        value={formData.ticketType}
                        options={ticketTypeOptions}
                        onChange={(e) =>
                            handleChange("ticketType", e.target.value)
                        }
                        error={errors.ticketType}
                        required
                        disabled={fieldsDisabled || isEditMode}
                        placeholder={t("tickets.select-ticket-type")}
                    />
                </Col>
                <Col md={6}>
                    <DropdownAtom
                        label={t("tickets.status.status")}
                        value={formData.status}
                        options={statusOptions}
                        onChange={(e) =>
                            handleChange("status", e.target.value)
                        }
                        required
                        sortOptions={false}
                        // Status is orthogonal to approval: it stays editable for
                        // editors even after the ticket is approved (so it can move
                        // through its lifecycle, e.g. → CLOSED). Only the content is
                        // locked on approval.
                        disabled={loading || !canEditTicket}
                    />
                </Col>
            </Row>

            <Row>
                <Col md={12}>
                    <TextFieldAtom
                        label={t("tickets.summary")}
                        value={formData.summary}
                        onChange={(e) =>
                            handleChange("summary", e.target.value)
                        }
                        error={errors.summary}
                        required
                        maxLength={500}
                        placeholder={t("tickets.enter-summary")}
                        disabled={fieldsDisabled}
                    />
                </Col>
            </Row>

            <Row>
                <Col md={12}>
                    <TextAreaAtom
                        label={t("tickets.description")}
                        value={formData.description}
                        onChange={(e) =>
                            handleChange("description", e.target.value)
                        }
                        placeholder={t("tickets.enter-description")}
                        rows={4}
                        disabled={fieldsDisabled}
                    />
                </Col>
            </Row>

            <Row>
                <Col md={6}>
                    <DropdownAtom
                        label={t("projects.tickets.assignee")}
                        value={formData.assignedTo}
                        options={assigneeOptions}
                        onChange={(e) =>
                            handleChange("assignedTo", e.target.value)
                        }
                        error={errors.assignedTo}
                        required={requireTicketApproval}
                        // Setting the assignee at creation is part of creating
                        // the ticket, so it's always editable on create; the
                        // tickets.edit gate only applies to existing tickets.
                        disabled={
                            fieldsDisabled || (isEditMode && !canEditAssignee)
                        }
                        placeholder={t("projects.tickets.unassigned")}
                    />
                </Col>
                <Col md={6}>
                    <DatePickerAtom
                        label={t("tickets.deadline")}
                        value={formData.deadline}
                        onChange={(date) => handleChange("deadline", date)}
                        placeholder={t("tickets.select-deadline")}
                        disabled={fieldsDisabled}
                    />
                </Col>
            </Row>

            <Row>
                <Col md={6}>
                    <TextFieldAtom
                        label={t("tickets.amount")}
                        type="number"
                        step="0.01"
                        value={formData.amount}
                        onChange={(e) =>
                            handleChange("amount", e.target.value)
                        }
                        error={errors.amount}
                        required
                        placeholder={t("tickets.enter-amount")}
                        suffix="€"
                        className="text-end"
                        disabled={fieldsDisabled}
                    />
                </Col>
            </Row>
            {isApproved && (
                <p className="text-muted small mb-0">
                    {t(
                        canEditTicket
                            ? "projects.tickets.approved-content-locked"
                            : "projects.tickets.approved-readonly",
                    )}
                </p>
            )}
        </>
    );

    const customButtons = showApproveButton
        ? [
              {
                  text: t("tickets.approve"),
                  variant: "success",
                  onClick: () => onApprove?.(ticket),
                  disabled: loading,
              },
          ]
        : [];

    return (
        <ModalAtom
            show={show}
            handleClose={handleClose}
            handleSubmit={handleSubmit}
            title={modalTitle}
            body={modalBody}
            saveText={loading ? "common.loading" : (isEditMode ? "update" : "create")}
            disabled={loading}
            // Keep Save available on approved tickets for editors so the status
            // change can be persisted; hide it only for pure viewers.
            hideSaveButton={isApproved && !canEditTicket}
            customButtons={customButtons}
            size="lg"
        />
    );
};

export default TicketFormModal;
